export interface Rung {
  offset: number
  template: string
}

/**
 * Choose which rung of the reminder ladder to fire, if any.
 *
 * Only the single latest rung that is due is ever a candidate. It is
 * deliberately NOT "the latest rung not yet sent": once a later rung has fired,
 * the earlier ones are spent, not pending. Backfilling them would send a stale
 * invoice one email per sweep until the ladder drained — which a client
 * experiences as harassment, and which is the bug this function exists to
 * prevent. See tests/reminders.test.ts.
 *
 * Pure by design so it can be tested without the Workers runtime.
 *
 * @param elapsed days since the due date; negative before it
 * @param sentOffsets offsets already recorded in reminder_log for this invoice
 */
export function pickRung(offsets: Rung[], elapsed: number, sentOffsets: number[]): Rung | null {
  const due = offsets.filter((o) => o.offset <= elapsed).sort((a, b) => b.offset - a.offset)
  const latest = due[0]
  if (!latest) return null
  return sentOffsets.includes(latest.offset) ? null : latest
}

/** Human label for a ladder rung, used in the settings UI. */
export function rungLabel(offset: number): string {
  if (offset < 0) {
    const n = Math.abs(offset)
    return `${n} day${n === 1 ? '' : 's'} before due`
  }
  if (offset === 0) return 'On the due date'
  return `${offset} day${offset === 1 ? '' : 's'} overdue`
}
